import useFormatter from "@/hooks/use-formatter.hook";
import NotFound from "@/pages/NotFound.tsx";
import { useOfferSystemFieldValue } from "@/pages/recruit/offers/components/use-offer-system-field-value.ts";
import { FullScreenCard } from "@/pages/security/components/FullScreenCard.tsx";
import { usePublicOffer } from "@/queries/recruit/use-offer.query.ts";
import { ApiFieldTypes } from "@/types/custom-field.types.ts";
import { ApiOfferPublicField } from "@/types/recruit/offer.types";
import {
  Avatar,
  Box,
  HtmlViewer,
  LinkTo,
  ListItem,
  Skeleton,
  Stack,
  Text,
  Title,
} from "@skailer-com/uikit";
import { Fragment, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

export function PublicOfferView() {
  const { uuid, offerId } = useParams();
  const { renderEmployeeName } = useFormatter();
  const getSystemValue = useOfferSystemFieldValue();
  const { t } = useTranslation();

  const { data: offer, isLoading } = usePublicOffer(uuid, offerId);

  if (isLoading) {
    return (
      <Box className="p-6">
        <Stack gap="lg">
          <Skeleton className="h-16 w-64" />
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-48 w-full" />
        </Stack>
      </Box>
    );
  }

  if (!offer) {
    return <NotFound />;
  }

  const renderField = ({ slug, title }: ApiOfferPublicField): ReactNode | undefined => {
    const customField = offer.custom_fields.find((field) => field.slug === slug);
    if (customField) {
      return (
        <ListItem
          preTitle={title}
          title={customField.url ? undefined : customField.value}
          titleSlot={
            customField.url ? (
              <LinkTo
                to={customField.url}
                target={"_blank"}
                variant={"secondary"}
                className={"truncate text-title"}
              >
                {customField.value}
              </LinkTo>
            ) : undefined
          }
        />
      );
    }

    const type = Object.values(ApiFieldTypes).find((fieldType) => fieldType === slug);
    const value =
      type &&
      getSystemValue(type, {
        date_start: offer.date_start,
        legal_entity: offer.legal_entity?.title ?? null,
        location: offer.location?.title ?? null,
        manager: offer.manager ? renderEmployeeName(offer.manager) : null,
        salary: offer.salary
          ? {
              salary_type: offer.salary.salary_type,
              salary_value: offer.salary.salary_value,
              currency_code: offer.salary.currency?.code ?? null,
            }
          : null,
      });

    return value ? (
      <ListItem preTitle={title} title={value.title} caption={value.caption} />
    ) : undefined;
  };

  const sections = offer.sections
    .map((section) => ({
      title: section.title,
      items: section.fields
        .map((field) => ({ slug: field.slug, node: renderField(field) }))
        .filter(({ node }) => node),
    }))
    .filter(({ items }) => items.length);

  return (
    <FullScreenCard>
      <Stack gap={"sm"} className={"w-full sm:max-w-xl"}>
        <Box className="w-full p-12 lg:min-h-[842px] bg-light rounded-xs shadow-md ">
          <Stack direction="horizontal" gap={"2xl"}>
            <Box className="mt-14">
              <Avatar
                url={offer?.organization.logo_url}
                acronym={offer?.organization.title ?? undefined}
                size="xl"
              />
            </Box>
            <Stack gap="2xl">
              <Title
                size={"xs"}
                header={t("ats:organization_invites", {
                  organization: offer?.organization.title,
                })}
              />

              <Stack gap={"md"}>
                <Stack>
                  <Title header={`${offer.first_name} ${offer.last_name}`} size="lg" />
                  <Text className="text-secondary">{offer.job_title}</Text>
                </Stack>

                {offer.offer_text && (
                  <Stack gap="smd">
                    <HtmlViewer content={offer.offer_text} />
                  </Stack>
                )}
                {sections.map(({ title, items }, index) => (
                  <Stack key={index}>
                    <Title size="xs" header={title} />
                    {items.map(({ slug, node }) => (
                      <Fragment key={slug}>{node}</Fragment>
                    ))}
                  </Stack>
                ))}
              </Stack>
            </Stack>
          </Stack>
        </Box>
      </Stack>
    </FullScreenCard>
  );
}
